import {equipmentBilling} from './billing-types'
import {chargeTotal} from './financial-line-item-form-utils'
import type {
    CreateBillableLineItemInput,
    JobAssetAssignmentInput,
    JobAssetRequirementInput,
    JobDetail as OperationalJobDetail,
    JobListItem as OperationalJobListItem,
    JobStatus,
    JobWorkerAssignmentInput,
    JobWorkerRequirementInput,
} from './schemas'
import type {FinancialLineItemInput} from './types'
import type {
    AssignmentInput,
    JobDetail,
    JobDetailFinancialLineItem,
    JobLifecycleStatus,
    JobListItem,
    RequirementInput,
} from './types'

const jobStatusToLegacy: Record<JobStatus, JobLifecycleStatus> = {
    planned: 'estimate',
    active: 'in_progress',
    completed: 'closed',
    cancelled: 'canceled',
}

const legacyStatusToJob: Record<JobLifecycleStatus, JobStatus> = {
    estimate: 'planned',
    in_progress: 'active',
    closed: 'completed',
    canceled: 'cancelled',
}

/** Map API job status (`planned` / `active` / …) to shared lifecycle labels. */
export function toLifecycleStatus(status: JobStatus): JobLifecycleStatus {
    return jobStatusToLegacy[status]
}

function jobWasApprovedForDispatch(job: OperationalJobDetail): boolean {
    if (job.status === 'active' || job.status === 'completed') {
        return true
    }
    if (job.status === 'cancelled') {
        return false
    }
    return false
}

function jobIsTerminal(status: JobStatus): boolean {
    return status === 'completed' || status === 'cancelled'
}

export function mapOperationalJobListItem(job: OperationalJobListItem): JobListItem {
    return {
        id: job.id,
        name: job.name,
        customerId: job.customerId,
        customerName: job.customerName,
        serviceTypeId: job.serviceTypeId,
        serviceTypeName: job.serviceTypeName,
        notes: job.notes,
        createdAt: job.createdAt,
        startDate: job.startDate,
        startTime: job.startTime,
        endDate: job.endDate,
        endTime: job.endTime,
        status: jobStatusToLegacy[job.status],
        approvedToStart: job.status === 'active' || job.status === 'completed',
        archivedAt: jobIsTerminal(job.status) ? job.createdAt : null,
        displayId: job.displayId,
        poNumber: job.poNumber,
        projectBillingTermsId: job.projectBillingTermsId,
        ...(job.dateTbc ? {dateTbc: true} : {}),
        ...(job.partialPickup ? {partialPickup: job.partialPickup} : {}),
    }
}

export function mapBillableToFinancial(
    lineItem: OperationalJobDetail['billableLineItems'][number],
): JobDetailFinancialLineItem {
    return {
        id: lineItem.id,
        jobRequirementId: null,
        chargeType: lineItem.chargeType,
        name: lineItem.name,
        description: lineItem.description,
        quantity: lineItem.quantity,
        unit: lineItem.unit,
        unitPrice: lineItem.unitPrice,
        totalPrice: lineItem.totalPrice,
        invoiceId: lineItem.invoiceId,
        isSynced: lineItem.isSynced,
    }
}

export function mapFinancialToBillable(input: FinancialLineItemInput): CreateBillableLineItemInput {
    return {
        chargeType: input.chargeType ?? 'manual',
        name: input.name,
        description: input.description,
        quantity: input.quantity,
        unit: input.unit,
        unitPrice: input.unitPrice,
        totalPrice: chargeTotal(input.quantity, input.unitPrice),
    }
}

export function mapOperationalJobDetail(job: OperationalJobDetail): JobDetail {
    return {
        id: job.id,
        projectId: job.projectId,
        name: job.name,
        description: job.description,
        serviceTypeId: job.serviceTypeId,
        serviceTypeName: job.serviceTypeName,
        notes: job.notes,
        customerId: job.customerId,
        customerName: job.customerName,
        displayId: job.displayId,
        poNumber: job.poNumber,
        projectBillingTermsId: job.projectBillingTermsId,
        billingMethod: job.billingMethod,
        createdAt: job.createdAt,
        customerContact: job.customerContact,
        customerContactSource: job.customerContactSource,
        serviceScheduleId: job.serviceScheduleId,
        scheduleRole: job.scheduleRole,
        startDate: job.startDate,
        startTime: job.startTime,
        endDate: job.endDate,
        endTime: job.endTime,
        dateTbc: job.dateTbc,
        ...(job.bookByDay ? {bookByDay: true} : {}),
        specificType: job.specificType ?? null,
        plannedEndDate: job.plannedEndDate ?? null,
        ...(job.cancelReason ? {cancelReason: job.cancelReason} : {}),
        ...(job.didNotHappenAt ? {didNotHappenAt: job.didNotHappenAt} : {}),
        ...(job.didNotHappenByName ? {didNotHappenByName: job.didNotHappenByName} : {}),
        ...(job.warnings?.length ? {warnings: job.warnings} : {}),
        projectName: job.projectName,
        projectJobCount: job.projectJobCount,
        orderNumber: job.orderNumber,
        repId: job.repId,
        repName: job.repName,
        projectCreatedById: job.projectCreatedById,
        createdById: job.createdById,
        isPickup: job.isPickup,
        ...(job.partialPickup ? {partialPickup: job.partialPickup} : {}),
        pickupConfirmedAt: job.pickupConfirmedAt,
        pickupConfirmedByName: job.pickupConfirmedByName,
        status: jobStatusToLegacy[job.status],
        approvedToStart: jobWasApprovedForDispatch(job),
        archivedAt: jobIsTerminal(job.status) ? job.createdAt : null,
        locations: job.locations.map((location) => ({
            id: location.id,
            name: location.name,
            latitude: location.latitude ?? 0,
            longitude: location.longitude ?? 0,
            notes: location.notes,
            ...(location.addressText ? {addressText: location.addressText} : {}),
        })),
        comments: job.comments.map((comment) => ({
            id: comment.id,
            content: comment.content,
            createdAt: comment.createdAt,
            createdById: comment.createdById,
            createdByName: comment.createdByName,
            updatedAt: comment.updatedAt,
            updatedById: comment.updatedById,
            updatedByName: comment.updatedByName,
        })),
        requirements: [
            ...job.workerRequirements.map((requirement) => ({
                id: requirement.id,
                requirementType: 'worker' as const,
                quantity: requirement.quantity,
                rentalAssetTypeId: null,
                rentalAssetTypeName: requirement.workerTypeName,
                workerTypeId: requirement.workerTypeId,
                billingRate: requirement.billingRate ?? null,
                billingType: requirement.billingType ?? null,
                estimatedHours: requirement.estimatedHours,
            })),
            ...job.assetRequirements.map((requirement) => ({
                id: requirement.id,
                requirementType: 'rental_asset' as const,
                quantity: requirement.quantity,
                rentalAssetTypeId: requirement.rentalAssetTypeId,
                rentalAssetTypeName: requirement.rentalAssetTypeName,
                billingRate: requirement.billingRate,
                billingType: requirement.billingType,
            })),
        ],
        stockEquipmentGroups: job.stockEquipmentGroups,
        assignments: [
            ...job.workerAssignments.map((assignment) => ({
                id: assignment.id,
                workerId: assignment.workerId,
                workerName: assignment.workerName,
                rentalAssetId: null,
                rentalAssetName: null,
                rentalAssetTypeId: null,
            })),
            ...job.assetAssignments.map((assignment) => ({
                id: assignment.id,
                workerId: null,
                workerName: null,
                rentalAssetId: assignment.rentalAssetId,
                rentalAssetName: assignment.rentalAssetName,
                rentalAssetTypeId: assignment.rentalAssetTypeId,
                isThirdParty: assignment.isThirdParty,
                unitNumber: assignment.unitNumber,
                hasUnitNumbers: assignment.hasUnitNumbers,
                pendingOnRent: assignment.pendingOnRent,
            })),
        ],
        financialLineItems: job.billableLineItems.map(mapBillableToFinancial),
    }
}

export function mapLegacyRequirementsToOperational(
    requirements: RequirementInput[],
    existingJob?: JobDetail,
): {
    workerRequirements: JobWorkerRequirementInput[]
    assetRequirements: JobAssetRequirementInput[]
} {
    const existingWorkerTypeId =
        existingJob?.requirements.find(
            (requirement) => requirement.requirementType === 'worker' && requirement.workerTypeId,
        )?.workerTypeId ?? null

    const workerRequirements: JobWorkerRequirementInput[] = []
    const assetRequirements: JobAssetRequirementInput[] = []

    for (const requirement of requirements) {
        if (requirement.requirementType === 'worker') {
            const workerTypeId =
                requirement.workerTypeId ??
                existingJob?.requirements.find((entry) => entry.id === requirement.id)
                    ?.workerTypeId ??
                existingWorkerTypeId

            if (!workerTypeId) {
                continue
            }

            workerRequirements.push({
                id: requirement.id,
                workerTypeId,
                quantity: requirement.quantity,
                billingRate: requirement.billingRate,
                billingType: requirement.billingType,
            })
            continue
        }

        if (requirement.rentalAssetTypeId) {
            assetRequirements.push({
                id: requirement.id,
                rentalAssetTypeId: requirement.rentalAssetTypeId,
                quantity: requirement.quantity,
                ...equipmentBilling(requirement),
            })
        }
    }

    return {workerRequirements, assetRequirements}
}

export function mapLegacyAssignmentsToOperational(
    assignments: AssignmentInput[],
    job: OperationalJobDetail,
): {
    workerAssignments: JobWorkerAssignmentInput[]
    assetAssignments: JobAssetAssignmentInput[]
} {
    const inheritedSchedule = {
        startDate: job.startDate,
        startTime: job.startTime,
        endDate: null,
        endTime: null,
    }

    const workerAssignments: JobWorkerAssignmentInput[] = []
    const assetAssignments: JobAssetAssignmentInput[] = []

    for (const assignment of assignments) {
        if (assignment.workerId) {
            const existing = job.workerAssignments.find(
                (entry) => entry.workerId === assignment.workerId,
            )
            // Keep existing dates, else use the requested days or the job schedule
            const days = assignment.days
            workerAssignments.push({
                id: existing?.id,
                workerId: assignment.workerId,
                ...(existing
                    ? {
                          startDate: existing.startDate,
                          startTime: existing.startTime,
                          endDate: existing.endDate,
                          endTime: existing.endTime,
                      }
                    : days
                      ? {...inheritedSchedule, startDate: days.from, endDate: days.until}
                      : inheritedSchedule),
            })
            continue
        }

        if (assignment.thirdPartyOfferingId) {
            assetAssignments.push({
                thirdPartyRentalAssetOfferingId: assignment.thirdPartyOfferingId,
                ...(assignment.quantity ? {quantity: assignment.quantity} : {}),
                ...inheritedSchedule,
            })
            continue
        }

        if (assignment.rentalAssetId) {
            const existing = job.assetAssignments.find(
                (entry) => entry.rentalAssetId === assignment.rentalAssetId,
            )
            assetAssignments.push({
                id: existing?.id,
                rentalAssetId: assignment.rentalAssetId,
                notes: existing?.notes ?? null,
                ...(existing
                    ? {
                          startDate: existing.startDate,
                          startTime: existing.startTime,
                          endDate: existing.endDate,
                          endTime: existing.endTime,
                      }
                    : inheritedSchedule),
            })
        }
    }

    return {workerAssignments, assetAssignments}
}

export function mapLegacyStatusFilters(
    statuses: JobLifecycleStatus[] | undefined,
): JobStatus[] | undefined {
    if (!statuses?.length) {
        return undefined
    }
    return statuses.map((status) => legacyStatusToJob[status])
}

export type {JobLifecycleStatus} from './types'
